import React, { useState, useEffect } from 'react'
import { createUseStyles, ThemeProvider } from 'react-jss'

import { normalizeResponse } from '../../utils/normalize'

import View from '../form/View'
import Background from '../form/Background'

import WelcomeStep from '../steps/WelcomeStep'
import PersonalStep from '../steps/PersonalStep'
import IdentificationStep from '../steps/IdentificationStep'
import DecisionStep from '../steps/DecisionStep'
import PictureStep from '../steps/PictureStep'
import DocumentStep from '../steps/DocumentStep'
import ResponsibleStep from '../steps/ResponsibleStep'
import FinishStep from '../steps/FinishStep'

import TermsScreen from '../terms/TermsScreen'

const useStyles = createUseStyles({
  root: {
    height: '100%',
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  content: {
    flex: 1,
    height: '100%',
    width: '100%',
    justifyContent: 'center',
    overflowX: 'hidden',

    '@media(min-width: 1000px)': {
      background: '#ffffff',
      position: 'relative',
      flex: 'none',
      width: '50%',
      minWidth: '700px',
      height: '75%',
      minHeight: '600px',
      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
      borderRadius: '16px',
      zIndex: 10,
    }
  },
  logo: {
    display: 'none',
    '@media(min-width: 1000px)': {
      width: '100%',
      display: 'block',
      textAlign: 'center',
      justifyContent: 'center',
    }
  },
  img: {
    width:'75%',
    maxWidth: '200px',
    marginTop: '40px',
  },
  transparent: {
    opacity: 0.0
  }
})

function Wizard (props) {
  const styles = useStyles()
  const [ flow, setFlow ] = useState()
  const [ flowId, setFlowId ] = useState()
  const [ flowStack, setFlowStack ] = useState()
  const [ stepIndex, setStepIndex ] = useState()
  const [ flowResponses, setFlowResponses ] = useState(new Map())

  const { flows, theme } = props.invite

  useEffect(() => {
    const initialFlowId = 'main'
    setFlowId(initialFlowId)
    setFlowStack([ initialFlowId ])
    setStepIndex(0)
    setFlow(flows.find(({ id }) => id === initialFlowId))
  }, [ flows ])

  const setResponse = response => {
    if (response) {
      let stepResponses = flowResponses.get(flowId)
      if (!stepResponses) {
        stepResponses = new Map()
        flowResponses.set(flowId, stepResponses)
      }
      const type = flow.steps[stepIndex].type
      stepResponses.set(stepIndex, { type, value: response })
    }
  }

  const unsetResponse = () => {
    const responses = flowResponses.get(flowId)
    if (responses) {
      responses.delete(stepIndex)
      if (responses.size === 0) {
        flowResponses.delete(flowId)
      }
    }
  }

  const getCurrentValue = () => {
    const responses = flowResponses.get(flowId)
    if (responses) {
      const stepResponse = responses.get(stepIndex)
      if (stepResponse && stepResponse.value) {
        return stepResponse.value
      }
    }
  }

  const next = response => {
    const step = flow.steps[stepIndex]
    setResponse(response)
    if (step.type === 'decision') {
      setFlowStack([ ...flowStack, flowId])
      setStepIndex(0)
      setFlowId(response.flow)
      setFlow(flows.find(({ id }) => id === response.flow))
    } else if (stepIndex >= flow.steps.length - 1) {
      const initialFlowId = 'main'
      setFlowStack([ initialFlowId ])
      setStepIndex(0)
      setFlow(flows.find(({ id }) => id === initialFlowId))
      setFlowId(initialFlowId)
      setFlowResponses(new Map())
    } else {
      setStepIndex(stepIndex + 1)
    }
  }

  const back = () => {
    unsetResponse()
    if (stepIndex) {
      setStepIndex(stepIndex - 1)
    } else if (flowStack.length > 1) {
      const previousFlowId = flowStack.pop()
      const previousFlow = flows.find(({ id }) => id === previousFlowId)
      setFlowStack([ ...flowStack ])
      setFlowId(previousFlowId)
      setStepIndex(previousFlow.steps.length - 1)
      setFlow(previousFlow)
    } else {
      console.log('Não existe fluxo anterior')
    }
  }


  const rendererComponent = () => {
    const step = flow.steps[stepIndex]
    const numOfSteps = flow.steps.length
    const { type, ...args } = step
    switch (type) {
      case 'welcome' :
        return <WelcomeStep 
          next={next}
          index={stepIndex}
          numOfSteps={numOfSteps}
          private={props.invite.private}
          { ...args }
        />

      case 'terms' : 
        return <TermsScreen
          next={next}
          back={back}
          index={stepIndex}
          numOfSteps={numOfSteps}
          {...args}
        />
  
      case 'identification' :
        return <IdentificationStep
          next={next}
          back={back}
          index={stepIndex}
          numOfSteps={numOfSteps}
          initialData={getCurrentValue()}
          {...args}
        />
  
      case 'personalData' :
        return <PersonalStep
          next={next}
          back={back}
          index={stepIndex}
          numOfSteps={numOfSteps}
          initialData={getCurrentValue()}
          {...args}
        />
  
      case 'decision' :
        return <DecisionStep
          next={next}
          back={back}
          index={stepIndex}
          numOfSteps={numOfSteps}
          initialData={getCurrentValue()}
          {...args}
        />
  
      case 'picture' :
        return <PictureStep
          next={next}
          back={back}
          index={stepIndex}
          numOfSteps={numOfSteps}
          initialData={getCurrentValue()}
          {...args}
        />
    
      case 'document' :
        return <DocumentStep
          next={next}
          back={back}
          index={stepIndex}
          numOfSteps={numOfSteps}
          initialData={getCurrentValue()}
          {...args}
        />
  
      case 'responsible':
        return <ResponsibleStep
          next={next}
          back={back}
          index={stepIndex}
          numOfSteps={numOfSteps}
          initialData={getCurrentValue()}
          {...args}
        />
        
      case 'finish' :
        return <FinishStep
          next={next}
          index={stepIndex}
          numOfSteps={numOfSteps}
          data={normalizeResponse(props.invite, flowResponses)}
          {...args}
        />  
  
      default:
        return <View></View>
    }
  }


  const imgStyles = (flow && flow.steps[stepIndex] && flow.steps[stepIndex].type === 'welcome') ? 
    `${styles.img} ${styles.transparent}` : styles.img


  return (
    <View className={styles.root}>
      <ThemeProvider theme={theme}>
        <Background />
        <View className={styles.content}>
          { flow && 
            rendererComponent()
          }
        </View>
        <View className={styles.logo}>
          { theme.logo && 
            <img 
              className={imgStyles} 
              src={theme.logoUrl} 
              alt="Logo" 
            />
          }
        </View>
      </ThemeProvider>
    </View>
  )
}

export default Wizard